import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorBoundary from '../../Containers/ErrorBoundary/ErrorBoundary';
import { useConversation, levels } from '../../../Data/Admin';
import { usePreference } from '../../../Data/Preference';
import { useSession } from '../../Session';
import Loading from '../../Loading';
import Icon from '../../Icons/Icon';
import Button from '../../Button/Button';
import { classNames, useVisibilityChange } from '../../../Services/helpers';
import Entries from '../../../Pages/Admin/Conversations/Entries';
import ReplyForm from '../../../Pages/Admin/Conversations/ReplyForm';
import { usePinnedConversation } from './pin';

const PinnedConversation = () => {
    return <ErrorBoundary fallback={<></>}>
        <Pinned />
    </ErrorBoundary>
}

export default PinnedConversation;

const Pinned = () => {
    const {pinnedConversationId, unpin} = usePinnedConversation();
    const {isAdmin} = useSession();
    const location = useLocation();

    if (!pinnedConversationId || !isAdmin(levels.SUPER)) {
        return null;
    }

    if (location.pathname === `/admin/conversations/${pinnedConversationId}`) {
        return null;
    }

    return <Panel conversationId={pinnedConversationId} unpin={unpin} />
}

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 520;
const COLLAPSED_WIDTH = 260;
const EDGE_PADDING = 8;

const Panel = ({conversationId, unpin}) => {
    const {conversation, addAdminMessage, refresh} = useConversation(conversationId);
    const [collapsed, setCollapsed] = usePreference('admin:pinnedConversationCollapsed', false);
    const [storedPosition, setStoredPosition] = usePreference('admin:pinnedConversationPosition', null);
    const [dragPosition, setDragPosition] = useState(null);
    const [viewport, setViewport] = useState(() => getViewport());
    const dragStateRef = useRef(null);
    const scrollRef = useRef(null);
    const panelRef = useRef(null);
    const [collapsedHeight, setCollapsedHeight] = useState(34);

    const size = {
        width: collapsed ? COLLAPSED_WIDTH : PANEL_WIDTH,
        height: collapsed ? collapsedHeight : PANEL_HEIGHT,
    };

    useLayoutEffect(() => {
        if (!collapsed || !panelRef.current) {
            return;
        }

        const measured = panelRef.current.offsetHeight;

        setCollapsedHeight((current) => (current === measured ? current : measured));
    }, [collapsed]);

    const position = useMemo(
        () => clampPosition(dragPosition ?? storedPosition ?? defaultPosition(viewport, size.width), size, viewport),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dragPosition, storedPosition, size.width, size.height, viewport],
    );

    useVisibilityChange(refresh, true);

    useEffect(() => {
        const onResize = () => setViewport(getViewport());

        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!scrollRef.current) {
            return;
        }

        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [conversation?.entries, collapsed]);

    const onPointerDown = (event) => {
        if (event.button !== 0 || event.target.closest('button')) {
            return;
        }

        dragStateRef.current = {
            pointerId: event.pointerId,
            offsetLeft: event.clientX - position.left,
            offsetTop: event.clientY - position.top,
        };

        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
        const drag = dragStateRef.current;

        if (!drag || drag.pointerId !== event.pointerId) {
            return;
        }

        setDragPosition({left: event.clientX - drag.offsetLeft, top: event.clientY - drag.offsetTop});
    };

    const onPointerUp = (event) => {
        if (dragStateRef.current?.pointerId !== event.pointerId) {
            return;
        }

        dragStateRef.current = null;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        setStoredPosition(position);
        setDragPosition(null);
    };

    const toggleCollapsed = () => {
        const next = !collapsed;

        setStoredPosition({
            left: position.left + (next ? PANEL_WIDTH - COLLAPSED_WIDTH : COLLAPSED_WIDTH - PANEL_WIDTH),
            top: position.top,
        });
        setCollapsed(next);
    };

    return <div ref={panelRef}
        className={classNames(
            'fixed z-50 flex flex-col surface-1 rounded-lg shadow-lg border border-brand overflow-hidden',
            'max-w-[calc(100vw-1rem)]',
        )}
        style={{
            left: position.left,
            top: position.top,
            width: size.width,
            height: collapsed ? undefined : PANEL_HEIGHT,
        }}
    >
        <div className="flex flex-row items-center gap-1 px-2 py-1 surface-2 cursor-move touch-none select-none shrink-0"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
        >
            <Icon icon="GripDots" size="1x" className="fill-muted shrink-0" />

            <span className="grow min-w-0 truncate text-sm font-semibold">
                {getTitle(conversation)}
            </span>

            <Button icon="ArrowUpRightFromSquare"
                theme="lightFlush"
                size="sm"
                zeroMargin
                noShadow
                availableOffline
                ariaLabel="Open the full conversation"
                link={`/admin/conversations/${conversationId}`}
            />
            <Button icon={collapsed ? 'UpChevron' : 'DownChevron'}
                theme="lightFlush"
                size="sm"
                zeroMargin
                noShadow
                availableOffline
                ariaLabel={collapsed ? 'Expand the pinned conversation' : 'Collapse the pinned conversation'}
                onClick={toggleCollapsed}
            />
            <Button icon="XMark"
                theme="lightFlush"
                size="sm"
                zeroMargin
                noShadow
                availableOffline
                ariaLabel="Unpin this conversation"
                onClick={unpin}
            />
        </div>

        {!collapsed && <>
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-3 prose prose-sm max-w-none">
                {conversation ?
                    <Entries entries={conversation.entries} files={conversation.files} /> :
                    <Loading center />
                }
            </div>

            {conversation && <div className="p-3 pt-0 shrink-0">
                <ReplyForm conversation={conversation} addAdminMessage={addAdminMessage} />
            </div>}
        </>}
    </div>
}

const getViewport = () => {
    return {width: window.innerWidth, height: window.innerHeight};
}

const defaultPosition = (viewport, width) => {
    return {
        left: viewport.width - width - 24,
        top: 80,
    };
}

const clampPosition = ({left, top}, size, viewport) => {
    const maxLeft = Math.max(EDGE_PADDING, viewport.width - size.width - EDGE_PADDING);
    const maxTop = Math.max(EDGE_PADDING, viewport.height - size.height - EDGE_PADDING);

    return {
        left: Math.min(Math.max(left, EDGE_PADDING), maxLeft),
        top: Math.min(Math.max(top, EDGE_PADDING), maxTop),
    };
}

const getTitle = (conversation) => {
    if (conversation?.user) {
        return `${conversation.user.firstName} ${conversation.user.lastName}`;
    }

    if (conversation?.business?.name) {
        return conversation.business.name;
    }

    return 'Pinned conversation';
}
